import {
  buildReportMeta,
  VIEW_TYPES,
  mapBudgetDetails,
  mapPrevExpenseDetails,
  mapSelectedFyExpenseDetails,
} from "./utilizationReportUtils";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const mapSubheadStatsToRow = (subhead) => {
  const prevBudget = toNumber(subhead.prev_budget_stats?.budget);
  const prevExpense = toNumber(subhead.prev_budget_stats?.expense);
  const currBudget = toNumber(subhead.curr_fy_stats?.budget);
  const expenseUptoPrevMonth = toNumber(subhead.curr_fy_stats?.lastMonthExpenses);
  const expenseForMonth = toNumber(subhead.curr_fy_stats?.currMonthExpense);
  const totalExpense = expenseUptoPrevMonth + expenseForMonth;
  const prevBalance = prevBudget - prevExpense;
  const selectedBalance = currBudget - totalExpense;
  const cumulativeBudget = prevBudget + currBudget;
  const cumulativeExpense = prevExpense + totalExpense;
  const cumulativeBalance = cumulativeBudget - cumulativeExpense;
  const expensePercentOfBudget =
    currBudget > 0 ? Math.round((totalExpense / currBudget) * 100) : 0;

  return {
    prevFy: {
      totalBudget: prevBudget,
      totalExpense: prevExpense,
      totalBalance: prevBalance,
      budgetDetails: mapBudgetDetails(subhead.prev_budget_stats?.details),
      expenseDetails: mapPrevExpenseDetails(subhead.prev_budget_stats?.details),
    },
    selectedFy: {
      totalBudget: currBudget,
      expenseUptoPrevMonth,
      expenseForMonth,
      totalExpense,
      expensePercentOfBudget,
      totalBalance: selectedBalance,
      budgetDetails: mapBudgetDetails(subhead.curr_fy_stats?.details),
      expenseDetails: mapSelectedFyExpenseDetails(subhead.curr_fy_stats?.details),
    },
    cumulative: {
      totalBudget: cumulativeBudget,
      totalExpense: cumulativeExpense,
      totalBalance: cumulativeBalance,
    },
  };
};

const extractDepartments = (apiData) => {
  if (Array.isArray(apiData)) return apiData;
  if (Array.isArray(apiData?.result)) return apiData.result;
  if (Array.isArray(apiData?.records)) return apiData.records;
  return [];
};

const mapDepartmentViewCards = (departments) =>
  departments.map((department) => ({
    id: String(department.id),
    title: department.name || "-",
    rows: (department.subheads ?? []).map((subhead, index) => ({
      slNo: index + 1,
      rowLabel: subhead.name || "-",
      ...mapSubheadStatsToRow(subhead),
    })),
  }));

const mapSubheadViewCards = (departments) => {
  const subheadMap = new Map();

  departments.forEach((department) => {
    (department.subheads ?? []).forEach((subhead) => {
      const key = String(subhead.id);
      if (!subheadMap.has(key)) {
        subheadMap.set(key, {
          id: key,
          title: subhead.name || "-",
          rows: [],
        });
      }

      subheadMap.get(key).rows.push({
        slNo: subheadMap.get(key).rows.length + 1,
        rowId: String(department.id),
        rowLabel: department.name || "-",
        ...mapSubheadStatsToRow(subhead),
      });
    });
  });

  return Array.from(subheadMap.values());
};

/**
 * Transform backend API payload into UI cards.
 * API returns departments with nested subheads; subhead view is pivoted on the client.
 */
const mapBackendResponseToCards = (apiData, viewType) => {
  const departments = extractDepartments(apiData);
  if (!departments.length) return [];

  if (viewType === VIEW_TYPES.SUBHEAD) {
    return mapSubheadViewCards(departments);
  }

  return mapDepartmentViewCards(departments);
};

/**
 * Builds UI-ready report data.
 * Meta always comes from page filters — never from API.
 */
export const normalizeUtilizationReportResponse = (apiData, filterContext) => ({
  meta: buildReportMeta(filterContext),
  cards: mapBackendResponseToCards(apiData, filterContext.viewType),
});

export { VIEW_TYPES };
